import React, { useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { formatDateForDisplay, formatTime } from '../../utils/dateUtils';

// キャンセル確認メール送信API呼び出し（ユーザーが自分でキャンセルした場合）
const sendCancelEmail = async (reservation) => {
  try {
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'reservation_cancelled_by_user',
        reservation: {
          code: reservation.reservation_code,
          name: reservation.customer_name,
          email: reservation.customer_email,
          phone: reservation.customer_phone,
          dateDisplay: formatDateForDisplay(reservation.reservation_date),
          time: formatTime(reservation.reservation_time),
          adultCount: reservation.adult_count,
          childCount: reservation.child_count,
          seatType: reservation.seat_type,
        }
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'メール送信に失敗しました');
    }

    return response.json();
  } catch (error) {
    console.error('キャンセルメール送信エラー:', error);
    // メール送信に失敗してもキャンセル自体は完了しているので続行
  }
};

function ReservationManagement() {
  const [searchPhone, setSearchPhone] = useState('');
  const [searchCode, setSearchCode] = useState('');
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchPhone.trim() || !searchCode.trim()) {
      alert('電話番号と予約番号を両方入力してください。');
      return;
    }

    setLoading(true);
    setSearched(true);
    try {
      const { data, error } = await supabaseClient.rpc('lookup_reservations', {
        p_phone: searchPhone.trim(),
        p_code: searchCode.trim().toLowerCase(),
      });

      if (error) throw error;
      setReservations(data || []);
    } catch (error) {
      console.error('検索エラー:', error);
      alert('検索に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (reservation) => {
    const reservationDate = new Date(reservation.reservation_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffDays = Math.ceil((reservationDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 1) {
      alert('当日のキャンセルはお電話でご連絡ください。\n電話: 0166-53-3032');
      return;
    }

    if (!window.confirm('この予約をキャンセルしますか？')) {
      return;
    }

    try {
      // 電話番号と予約番号が一致する予約のみ、サーバー側でキャンセルされる
      const { data: cancelled, error } = await supabaseClient.rpc('cancel_reservation', {
        p_id: reservation.id,
        p_phone: searchPhone.trim(),
        p_code: searchCode.trim().toLowerCase(),
      });

      if (error) throw error;
      if (!cancelled || cancelled.length === 0) {
        alert('キャンセルできませんでした。お電話でご連絡ください。\n電話: 0166-53-3032');
        return;
      }

      // キャンセル確認メールを送信（ユーザーが自分でキャンセルした場合）
      if (reservation.customer_email) {
        await sendCancelEmail(reservation);
      }

      alert('予約をキャンセルしました。');
      setReservations(prev => prev.map(r => 
        r.id === reservation.id ? { ...r, status: 'cancelled' } : r
      ));
    } catch (error) {
      console.error('キャンセルエラー:', error);
      alert('キャンセルに失敗しました。お電話でご連絡ください。');
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: '店舗確認中',
      confirmed: '確定',
      cancelled: 'キャンセル済',
      visited: '来店済み',
      no_show: '無断キャンセル'
    };
    return labels[status] || status;
  };

  const getStatusClass = (status) => {
    return `status-badge status-${status}`;
  };

  const canCancel = (reservation) => {
    if (reservation.status === 'cancelled' || reservation.status === 'visited' || reservation.status === 'no_show') {
      return false;
    }
    const reservationDate = new Date(reservation.reservation_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return reservationDate >= today;
  };

  return (
    <section id="reservation-management" className="reservation-management">
      <div className="container">
        <h2 className="section-title">予約確認・キャンセル</h2>
        <p className="reservation-notice">※予約の変更・キャンセルは予約日の2日前の24:00まで可能です。それ以降はお電話でのお問い合わせをお願いいたします。</p>
        
        <form className="search-form" onSubmit={handleSearch}>
          <div className="form-group">
            <label>電話番号 *</label>
            <input
              type="tel"
              value={searchPhone}
              onChange={(e) => setSearchPhone(e.target.value)}
              placeholder="09012345678"
            />
          </div>
          <div className="form-group">
            <label>予約番号 *</label>
            <input
              type="text"
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value.toLowerCase())}
              placeholder="仮予約メールに記載された番号"
              maxLength={4}
              style={{ textTransform: 'lowercase' }}
            />
          </div>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? '検索中...' : '検索'}
          </button>
        </form>

        {searched && (
          <div className="search-results">
            {reservations.length === 0 ? (
              <p className="no-results">予約が見つかりませんでした。</p>
            ) : (
              <div className="reservations-list">
                {reservations.map((reservation) => (
                  <div key={reservation.id} className="reservation-card">
                    <div className="reservation-header">
                      <span className={getStatusClass(reservation.status)}>
                        {getStatusLabel(reservation.status)}
                      </span>
                    </div>
                    <div className="reservation-details">
                      <p>
                        <strong>予約番号：</strong>
                        <span className="reservation-code-inline">{reservation.reservation_code}</span>
                      </p>
                      <p>
                        <strong>日時：</strong>
                        {formatDateForDisplay(reservation.reservation_date)} {formatTime(reservation.reservation_time)}
                      </p>
                      <p>
                        <strong>人数：</strong>
                        大人{reservation.adult_count}名
                        {reservation.child_count > 0 && `、子供${reservation.child_count}名`}
                      </p>
                      <p>
                        <strong>席：</strong>
                        {reservation.seat_type === 'counter' ? 'カウンター席' : 'テーブル席'}
                      </p>
                      <p>
                        <strong>お名前：</strong>
                        {reservation.customer_name}
                      </p>
                    </div>
                    {canCancel(reservation) && (
                      <div className="reservation-actions">
                        <button
                          className="btn-cancel"
                          onClick={() => handleCancel(reservation)}
                        >
                          キャンセル
                        </button>
                        <p className="cancel-note">
                          ※変更する場合は一度予約をキャンセルし、再度ご予約ください。
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default ReservationManagement;

